import {
  AMENDMENT_METHOD,
  AMENDMENT_TYPE,
  AMENDMENT_UNITS,
  AmendmentEvent as RDAmendmentEvent,
} from '@cibo/core'
import { DatePickerField, NumberInputField, SelectField } from '@cibo/ui'
import ClearIcon from '@mui/icons-material/Clear'
import EditRounded from '@mui/icons-material/EditRounded'
import {
  Button,
  Collapse,
  Divider,
  FormControl,
  FormLabel,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { Formik, FormikHelpers, useField } from 'formik'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { EventComponentProps } from '../../EventDetail'
import { useIsRequired } from '../../useIsRequired'

const AmendmentEventValidationSchema = () =>
  Yup.object().shape({
    date: Yup.date().required('Date is required').typeError('Date is required'),
    amendmentType: Yup.string().required('Type is required'),
    method: Yup.string().required('Method is required'),
    rate: Yup.number().required('Rate is required').min(0),
    units: Yup.string().required('Units are required'),
  })

export const AmendmentEventEditorSimple = ({
  editing,
  year,
  name,
  onEditEvent,
  onPressRemove,
  requirement,
  isImmutable,
}: EventComponentProps) => {
  const { t } = useTranslation('@cibo/landmanager/AmendmentEditor')
  const [{ value }, { initialValue, touched }, { setValue, setTouched }] =
    useField<RDAmendmentEvent>(name)
  const [localEditing, setLocalEditing] = useState(!initialValue)
  const isRequired = useIsRequired(requirement)

  useEffect(() => {
    onEditEvent(!initialValue)
  }, [!initialValue])

  const defaultMonth = dayjs(new Date(year - 1, 10))
  const validationSchema = AmendmentEventValidationSchema()

  const handleSubmit = (
    values: RDAmendmentEvent,
    { setSubmitting }: FormikHelpers<RDAmendmentEvent>
  ) => {
    setTouched(true, false)
    setValue(values)
    setLocalEditing(false)
    setSubmitting(false)
    onEditEvent(false)
  }

  const editingDisabled = editing || isImmutable

  return (
    <>
      <Collapse in={!localEditing}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            width: '275px',
          }}
        >
          <Stack spacing={1}>
            <Typography>{value.amendmentType ? t(`type_${value.amendmentType}`) : '--'}</Typography>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              divider={<Divider orientation="vertical" flexItem />}
            >
              {value.date && (
                <Typography variant="caption">
                  {t('eventDate', { date: dayjs(value.date, 'YYYY-MM-DD').toDate() })}
                </Typography>
              )}
              {value.rate !== undefined && (
                <Typography variant="caption">
                  {t('eventRate', { rate: value.rate })} {value.units && t(`units_${value.units}`)}
                </Typography>
              )}
            </Stack>
          </Stack>
          <Stack direction="column">
            <Tooltip title={t('edit')}>
              <IconButton
                aria-label={t('editEvent') as string}
                onClick={() => {
                  setLocalEditing(true)
                  onEditEvent(true)
                }}
                size="small"
                disabled={editingDisabled}
              >
                <EditRounded />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('remove')}>
              <IconButton
                aria-label={t('removeEvent') as string}
                onClick={onPressRemove}
                size="small"
                disabled={editingDisabled}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Collapse>
      <Formik
        initialValues={
          value || ({ date: null, amendmentType: '', method: '' } as unknown as RDAmendmentEvent)
        }
        validationSchema={validationSchema}
        validateOnMount
        enableReinitialize
        disabled={isImmutable}
        onSubmit={handleSubmit}
      >
        {({ isValid, resetForm, submitForm, values, dirty, errors }) => {
          return (
            <Collapse in={localEditing} timeout="auto" unmountOnExit>
              <Stack spacing={2}>
                <FormControl fullWidth required={isRequired('date')}>
                  <FormLabel>{t('date')}</FormLabel>
                  <DatePickerField
                    name={`date`}
                    minDate={dayjs(new Date(year - 1, 6, 1))}
                    maxDate={dayjs(new Date(year + 1, 0, 1))}
                    referenceDate={defaultMonth}
                    data-testid={`date`}
                    stringValue
                  />
                </FormControl>

                <FormControl fullWidth required>
                  <FormLabel>{t('type')}</FormLabel>
                  <SelectField
                    fieldName="amendmentType"
                    options={AMENDMENT_TYPE}
                    renderOption={option => t(`type_${option}`)}
                  />
                </FormControl>

                <FormControl fullWidth required>
                  <FormLabel>{t('method')}</FormLabel>
                  <SelectField
                    fieldName="method"
                    options={AMENDMENT_METHOD}
                    renderOption={option => t(`method_${option}`)}
                  />
                </FormControl>

                <FormControl fullWidth required={isRequired('rate')}>
                  <FormLabel>{t('rate')}</FormLabel>
                  <NumberInputField name={`rate`} data-testid={`rate`} min={0} />
                </FormControl>

                <FormControl fullWidth required>
                  <FormLabel>{t('units')}</FormLabel>
                  <SelectField
                    fieldName="units"
                    options={AMENDMENT_UNITS}
                    renderOption={option => t(`units_${option}`)}
                  />
                </FormControl>
                <Stack direction="row" justifyContent="flex-end" spacing={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    data-testid={`${name}-cancel`}
                    onClick={() => {
                      !initialValue && !touched && onPressRemove()
                      setLocalEditing(false)
                      onEditEvent(false)
                      resetForm()
                    }}
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    variant="contained"
                    data-testid={`${name}-add`}
                    size="small"
                    onClick={() => submitForm()}
                    disabled={!isValid || !dirty}
                  >
                    {!initialValue ? t('add') : t('saveEvent')}
                  </Button>
                </Stack>
              </Stack>
            </Collapse>
          )
        }}
      </Formik>
    </>
  )
}
